import { Container, Graphics } from 'pixi.js';
import { DOME_SCALE as SCALE } from '../../data/full/dome';
import type { SetDefinition } from '../../data/types';
import type { SimState } from '../../sim/state';
import type { Family, RenderContext } from '../context';
import { mixColor } from '../palette';
import { PANELS, RIBS, RIB_FROM, RING, TWINKLE_GROUPS, crownPosts, garlands } from './dome-layout';
import { decorSet, type Frame } from '../frame';
import { setTint, releaseDrawing } from '../util';

const WHITE = 0xffffff;
const TAU = Math.PI * 2;
const DARK_WOOD = 0x2a1a0c;
const PANEL_TOKENS = ['mage', 'turquoise', 'or', 'healer'] as const;

export function createDome(ctx: RenderContext): Family {
  const root = new Container();
  ctx.layers.decor.addChild(root);
  const panels = PANEL_TOKENS.map(() => new Graphics());
  const garland = [0, ...Array.from({ length: TWINKLE_GROUPS }, (_, i) => i + 1)].map(
    () => new Graphics(),
  );
  const ribs = new Graphics();
  const posts = new Graphics();
  root.addChild(...panels, ribs, ...garland, posts);

  let drawnKey = '';
  let styledAt = Number.NaN;

  function draw(set: SetDefinition, seed: number): void {
    const center = { x: set.arena.width / 2, y: set.arena.height / 2 };
    const step = TAU / RIBS;
    const at = (radius: number, angle: number): [number, number] => [
      center.x + Math.cos(angle) * radius,
      center.y + Math.sin(angle) * radius,
    ];

    for (const [index, panel] of panels.entries()) {
      panel.clear();
      for (let rib = index; rib < RIBS; rib += PANEL_TOKENS.length) {
        const a0 = rib * step + 0.02;
        const a1 = (rib + 1) * step - 0.02;
        panel.moveTo(...at(PANELS.from, a0));
        panel.lineTo(...at(PANELS.to, a0));
        panel.arc(center.x, center.y, PANELS.to, a0, a1);
        panel.lineTo(...at(PANELS.from, a1));
        panel.arc(center.x, center.y, PANELS.from, a1, a0, true);
        panel.closePath();
      }
      panel.fill({ color: WHITE, alpha: 0.1 });
      for (let rib = index; rib < RIBS; rib += PANEL_TOKENS.length) {
        const [x, y] = at((PANELS.from + PANELS.to) / 2, (rib + 0.5) * step);
        panel.circle(x, y, 5 * SCALE);
      }
      panel.stroke({ width: SCALE, color: WHITE, alpha: 0.3 });
    }

    ribs.clear();
    for (let rib = 0; rib < RIBS; rib += 1) {
      ribs.moveTo(...at(RIB_FROM, rib * step)).lineTo(...at(RING, rib * step));
    }
    ribs.stroke({ width: 1.2 * SCALE, color: WHITE, alpha: 0.6, cap: 'round' });
    ribs.circle(center.x, center.y, RING);
    ribs.stroke({ width: 3.5 * SCALE, color: WHITE });

    for (const shape of garland) {
      shape.clear();
    }
    for (const light of garlands(seed, center)) {
      garland[light.group]?.circle(light.x, light.y, light.radius * SCALE).fill(WHITE);
    }

    posts.clear();
    for (const post of crownPosts(set.obstacles ?? [], center)) {
      posts.circle(post.x, post.y, post.radius - 1.5).fill({ color: WHITE, alpha: 0.45 });
      posts.circle(post.x, post.y, post.radius - 1.5).stroke({ width: 3, color: WHITE });
      posts.circle(post.x, post.y, post.radius).stroke({ width: 1, color: DARK_WOOD, alpha: 0.5 });
      posts.circle(post.x, post.y, post.radius * 0.6);
      posts.circle(post.x, post.y, post.radius * 0.25);
      posts.stroke({ width: 1.2, color: WHITE, alpha: 0.6 });
    }
  }

  function style(frame: Frame): void {
    const { palette, light } = frame;
    styledAt = frame.fraction;
    const wood = mixColor(palette.or, DARK_WOOD, 0.45);
    const mode = light.additive ? 'add' : 'normal';
    for (const [index, panel] of panels.entries()) {
      setTint(panel, palette[PANEL_TOKENS[index] ?? 'or']);
      panel.blendMode = mode;
      panel.alpha = light.additive ? 1 : 0.6;
    }
    ribs.tint = wood;
    ribs.alpha = light.additive ? 0.75 : 0.5;
    posts.tint = wood;
    for (const shape of garland) {
      shape.tint = palette.noyau;
      shape.blendMode = mode;
    }
  }

  return {
    update(state: SimState, _alpha: number, frame: Frame): void {
      const set = decorSet(frame, 'dome');
      const key = `${set?.id ?? ''} ${String(state.seed)}`;
      if (set !== undefined && key !== drawnKey) {
        drawnKey = key;
        draw(set, state.seed);
      } else if (set === undefined && drawnKey !== '') {
        drawnKey = '';
        releaseDrawing(root);
      }
      root.visible = frame.set.decor === 'dome';
      if (!root.visible) {
        return;
      }
      if (frame.fraction !== styledAt) {
        style(frame);
      }
      for (const [index, shape] of garland.entries()) {
        shape.alpha =
          index === 0 || frame.calm
            ? 1
            : 0.35 + 0.65 * (0.5 + 0.5 * Math.sin(frame.now * 0.05 + index * 2.1));
      }
    },
    destroy(): void {
      root.destroy({ children: true });
    },
  };
}
